import { useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

const formatDisplay = (num: number, maxFractionDigits = 2): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: maxFractionDigits }).format(num)

// parse a possibly-grouped human number: the LAST-occurring separator ('.' or ',') is the decimal,
// the other is thousands grouping — so 1,234.56 and 1.234,56 both give 1234.56, 12,50 gives 12.5, and
// 1.999 stays 1.999 (a live-typed decimal, not EU thousands). replaces the old blanket comma→dot swap
// that mangled every grouped paste. returns null on a non-numeric fragment. (#302)
const parseNumeric = (raw: string): number | null => {
  const s = raw.replace(/[^\d,.-]/g, '')
  if (s === '' || s === '-' || s === '.' || s === ',') return null
  const lastComma = s.lastIndexOf(',')
  const lastDot = s.lastIndexOf('.')
  let normalized: string
  if (lastComma === -1 && lastDot === -1) {
    normalized = s
  } else if (lastComma > lastDot) {
    // comma is the decimal separator → drop dots (grouping), turn the decimal comma into a dot
    normalized = s.replace(/\./g, '').replace(/,/g, '.')
  } else {
    // dot is the decimal separator → drop commas (grouping)
    normalized = s.replace(/,/g, '')
  }
  const num = parseFloat(normalized)
  return isNaN(num) ? null : num
}

// detects whether the string contains arithmetic operators (beyond a leading minus)
const containsOperator = (str: string): boolean => {
  if (/[+*/()]/.test(str)) return true
  // check for minus that isn't just a leading minus
  const withoutLeading = str.replace(/^-/, '')
  return /-/.test(withoutLeading)
}

// safe recursive descent parser — no eval()
// grammar:
//   expression := term (('+' | '-') term)*
//   term       := factor (('*' | '/') factor)*
//   factor     := '(' expression ')' | '-' factor | number
const evaluateFormula = (input: string): number | null => {
  const tokens = input.replace(/\s/g, '').match(/(\d+\.?\d*|[+\-*/()])/g)
  if (!tokens) return null

  let pos = 0
  const peek = () => tokens[pos]
  const consume = () => tokens[pos++]

  const parseExpression = (): number | null => {
    let left = parseTerm()
    if (left === null) return null
    while (peek() === '+' || peek() === '-') {
      const op = consume()
      const right = parseTerm()
      if (right === null) return null
      left = op === '+' ? left + right : left - right
    }
    return left
  }

  const parseTerm = (): number | null => {
    let left = parseFactor()
    if (left === null) return null
    while (peek() === '*' || peek() === '/') {
      const op = consume()
      const right = parseFactor()
      if (right === null) return null
      left = op === '*' ? left * right : left / right
    }
    return left
  }

  const parseFactor = (): number | null => {
    if (peek() === '(') {
      consume() // '('
      const val = parseExpression()
      if (val === null || peek() !== ')') return null
      consume() // ')'
      return val
    }
    if (peek() === '-') {
      consume()
      const val = parseFactor()
      return val === null ? null : -val
    }
    return parseNumber()
  }

  const parseNumber = (): number | null => {
    const token = consume()
    if (token === undefined) return null
    const num = parseFloat(token)
    return isNaN(num) ? null : num
  }

  const result = parseExpression()
  if (pos !== tokens.length) return null
  if (result === null || !isFinite(result)) return null
  return result
}

interface MaskedNumberInputProps {
  value: number | undefined
  onChange: (value: number | undefined) => void
  onBlur?: () => void
  onFocus?: () => void
  // forwarded to the underlying input; e.g. to stop click propagation when the field
  // sits inside a clickable row that would otherwise toggle on click.
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void
  id?: string
  maxFractionDigits?: number
  disabled?: boolean
  // extra width/dimming classes merged after the component's own appearance classes
  className?: string
  'aria-label'?: string
  // shown when the field is empty (e.g. "none" for an optional pivot/limit)
  placeholder?: string
  // forwarded to the underlying input; inert on the text-mode field but kept for API parity
  min?: number
  max?: number
  step?: number | string
  'data-testid'?: string
}

export const MaskedNumberInput = ({
  value,
  onChange,
  onBlur,
  onFocus,
  onClick,
  id,
  maxFractionDigits = 2,
  disabled,
  className,
  'aria-label': ariaLabel,
  placeholder,
  min,
  max,
  step,
  'data-testid': dataTestId,
}: MaskedNumberInputProps) => {
  const [display, setDisplay] = useState(
    value === undefined ? '' : formatDisplay(value, maxFractionDigits)
  )
  const [focused, setFocused] = useState(false)
  const [shaking, setShaking] = useState(false)
  const shakeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const roundTo = (num: number) =>
    Math.round(num * 10 ** maxFractionDigits) / 10 ** maxFractionDigits

  const triggerShake = () => {
    setShaking(true)
    clearTimeout(shakeTimeoutRef.current)
    shakeTimeoutRef.current = setTimeout(() => setShaking(false), 400)
  }

  useEffect(() => () => clearTimeout(shakeTimeoutRef.current), [])

  useEffect(() => {
    if (!focused) {
      setDisplay(value === undefined ? '' : formatDisplay(value, maxFractionDigits))
    }
  }, [value, focused, maxFractionDigits])

  return (
    <Input
      id={id}
      data-testid={dataTestId}
      disabled={disabled}
      inputMode="decimal"
      aria-label={ariaLabel}
      placeholder={placeholder}
      min={min}
      max={max}
      step={step}
      className={cn(
        '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
        shaking && 'animate-shake',
        className
      )}
      value={display}
      onChange={(e) => {
        // keep both separators so parseNumeric can detect the decimal one (was: blanket comma→dot,
        // which mangled grouped pastes like 1,234.56). show the user's raw input while typing.
        const cleaned = e.target.value.replace(/[^\d.,+\-*/()]/g, '')
        setDisplay(cleaned)

        if (!containsOperator(cleaned)) {
          if (cleaned === '') {
            // an empty field holds no number — report undefined, not 0
            onChange(undefined)
          } else {
            const num = parseNumeric(cleaned)
            // round to maxFractionDigits so the committed value never exceeds the displayed precision
            // (typing 1.999 with maxFractionDigits=2 commits 2, not 1.999) (#302)
            onChange(num === null ? 0 : roundTo(num))
          }
        }
      }}
      onFocus={() => {
        setFocused(true)
        setDisplay(value === undefined ? '' : String(value))
        onFocus?.()
      }}
      onBlur={() => {
        setFocused(false)

        if (containsOperator(display)) {
          const result = evaluateFormula(display)
          if (result === null) {
            // invalid formula — revert the display to the committed value
            setDisplay(value === undefined ? '' : formatDisplay(value, maxFractionDigits))
          } else {
            const num = roundTo(result)
            if (num !== value) onChange(num)
            setDisplay(formatDisplay(num, maxFractionDigits))
          }
        } else {
          const parsed = parseNumeric(display)
          if (parsed === null) {
            // an empty (or non-numeric) field stays empty instead of becoming 0
            if (value !== undefined) onChange(undefined)
            setDisplay('')
          } else {
            // round before committing so the stored value matches the displayed precision (#302)
            const rounded = roundTo(parsed)
            if (rounded !== value) onChange(rounded)
            setDisplay(formatDisplay(rounded, maxFractionDigits))
          }
        }

        onBlur?.()
      }}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault()

        if (e.key === 'Enter' && containsOperator(display)) {
          e.preventDefault()
          const result = evaluateFormula(display)
          if (result !== null) {
            const rounded = roundTo(result)
            setDisplay(String(rounded))
            onChange(rounded)
          } else {
            triggerShake()
          }
        }
      }}
      onWheel={(e) => e.currentTarget.blur()}
    />
  )
}
